import { EventEmitter } from 'events';
import { Map } from 'immutable';

import AppDispatcher from '../AppDispatcher';
import Constants from '../Constants';
import newJobForOrganization from './specs/newJobForOrganization';

function parseBudgetConstraintPercent(budgetConstraint) {
  if (budgetConstraint.percent) {
    return Math.round(budgetConstraint.percent * 100);
  }
  return null;
}

class CrossChannelStore extends EventEmitter {
  constructor() {
    super();
    this._parameterSpecs = new Map();
    this._showAdvancedSettings = false;

    this.dispatchToken = AppDispatcher.register((action) => {
      switch (action.actionType) {
        case Constants.CrossChannel.UPDATE_REQUESTED:
          this._setValue(action.organizationId, action.key, action.newValue);
          this.emitChange();
          break;
        case Constants.CrossChannel.TOGGLE_ADVANCED:
          this._toggleAdvanced();
          this.emitChange();
          break;
        case Constants.CrossChannel.ADDVALUE_REQUESTED:
          this._addValue(action.organizationId, action.key, action.addValue);
          this.emitChange();
          break;
        case Constants.CrossChannel.REMOVEVALUE_REQUESTED:
          this._removeValue(action.organizationId, action.key, action.removeValue);
          this.emitChange();
          break;
        case Constants.CrossChannel.UPDATE_MAP:
          this._updateMap(action.organizationId, action.map, action.key, action.value);
          this.emitChange();
          break;
        case Constants.CrossChannel.UPDATE_MODE:
          this._updateModeOption(action.organizationId, action.mode,
            action.attribute, action.value);
          this.emitChange();
          break;
        case Constants.CrossChannel.RESET_REQUESTED:
          this._reset(action.organizationId);
          this.emitChange();
          break;
        case Constants.CrossChannel.SCENARIO_CLONED:
          this._cloneScenario(action.organizationId, action.configs);
          this.emitChange();
          break;
        case Constants.CrossChannel.SCENARIO_SAVED:
          // API call fires
          break;
        default:
          // do nothing
      }
    });
  }

  getJobForOrganization(organizationId) {
    return this._parameterSpecs
      .get(organizationId, newJobForOrganization(organizationId));
  }

  _setValue(organizationId, key, value) {
    this._parameterSpecs = this._parameterSpecs
      .set(organizationId,
        this.getJobForOrganization(organizationId)
        .set(key, value));
  }

  _toggleAdvanced() {
    this._showAdvancedSettings = !this._showAdvancedSettings;
  }

  _addValue(organizationId, key, value) {
    this._setValue(organizationId,
      key, this.getJobForOrganization(organizationId).get(key).add(value));
  }

  _removeValue(organizationId, key, value) {
    this._setValue(organizationId,
      key, this.getJobForOrganization(organizationId).get(key).remove(value));
  }

  _updateMap(organizationId, map, key, value) {
    const newMap = this.getJobForOrganization(organizationId).get(map).set(key, value);
    this._setValue(organizationId, map, newMap);
  }

  _cloneScenario(organizationId, configs) {
    // Pre-fill out the form
    this._reset(organizationId);

    // Set non-mode parameters
    for (const key in configs) {
      if (key !== 'modes' && key !== 'id') {
        this._setValue(organizationId, key, configs[key]);
      }
    }

    // Set modes
    configs.modes.forEach((mode) => {
      this._updateModeOption(organizationId, mode.key, 'selected', true);
      this._updateModeOption(organizationId, mode.key, 'cost', mode.cost);
      this._updateModeOption(organizationId, mode.key, 'frequency', mode.frequencyCap || null);
      this._updateModeOption(organizationId, mode.key, 'impactFactor', mode.relativeImpact);
      this._updateModeOption(organizationId, mode.key, 'discount', mode.discountFactor);
      this._updateModeOption(organizationId, mode.key, 'flightSize', mode.flightSize);

      // Only set budgetConstraints if attributes are present
      if (mode.budgetConstraint) {
        this._updateModeOption(organizationId, mode.key, 'percentageType', mode.budgetConstraint.type);
        this._updateModeOption(organizationId, mode.key, 'percentage', parseBudgetConstraintPercent(mode.budgetConstraint));
      }
    });

    // set it up
    this.getJobForOrganization(organizationId);
  }

  _updateModeOption(organizationId, modeKey, attribute, value) {
    const newModeOption = this.getJobForOrganization(organizationId)
      .get('modeOptions')
      .get(modeKey)
      .set(attribute, value);
    this._updateMap(organizationId, 'modeOptions', modeKey, newModeOption);
  }

  _reset(organizationId) {
    this._parameterSpecs = this._parameterSpecs.delete(organizationId);
  }

  emitChange() {
    this.emit('CHANGE');
  }

  addChangeListener(cb) {
    this.on('CHANGE', cb);
  }

  removeChangeListener(cb) {
    this.removeListener('CHANGE', cb);
  }

  showAdvancedSettings() {
    return this._showAdvancedSettings;
  }
}

export default new CrossChannelStore();
